import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid, Briefcase, Layers, Receipt, Wallet, Inbox, FolderClosed,
  Settings, HelpCircle, LogOut, ChevronLeft, ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuthStore } from "../../store/useAuthStore";
import { useChatStore } from "../../store/useChatStore";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";

export const CLIENT_NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutGrid, path: "/portal" },
  { key: "projects", label: "Projects", icon: Briefcase, path: "/portal/projects" },
  { key: "services", label: "Services", icon: Layers, path: "/portal/services" },
  { key: "invoices", label: "Invoices", icon: Receipt, path: "/portal/invoices" },
  { key: "wallet", label: "Wallet", icon: Wallet },
  { key: "inbox", label: "Inbox", icon: Inbox, path: "/portal/inbox" },
  { key: "files", label: "Files", icon: FolderClosed, path: "/portal/files" },
];

interface Props {
  active: string;
  navRefs?: React.MutableRefObject<(HTMLButtonElement | null)[]>;
}

// Shared Client Workspace sidebar used by the Dashboard and Services pages.
// Modules disabled for this Brand Environment never render (the API blocks
// them too — this is the usability half of the same rule).
const featureEnabled = (features: any, key: string) => !features || features[key] !== false;

const ClientSidebar = ({ active, navRefs }: Props) => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { collapsed, toggle } = useSidebarCollapsed("cre-client-sidebar");
  const soon = () => toast("This section is coming soon.", { icon: "🔜" });
  const mini = collapsed;

  // Total unread across all conversations → badge on the Inbox nav item.
  // Refetched whenever the sidebar mounts (i.e. on each portal-page navigation),
  // so the count stays current without touching ChatContainer's live socket
  // subscription (which shares a module-level handler).
  const { users, getUsers } = useChatStore();
  useEffect(() => { getUsers(); }, [getUsers]);
  const tenantBrand: any = useAuthStore((s) => s.authUser?.tenant) || null;
  const totalUnread = users.reduce((sum, u) => sum + (u.unreadCount || 0), 0);

  return (
    <aside className={`hidden lg:flex flex-col bg-white border-r border-outline-variant shrink-0 sticky top-0 h-screen z-30 transition-[width] duration-200 ${mini ? "w-[72px]" : "w-60"}`}>
      <div className={`py-5 flex items-center gap-1.5 ${mini ? "px-4 justify-center" : "px-6"}`}>
        {tenantBrand?.logoUrl ? (
          <img src={tenantBrand.logoUrl} alt={tenantBrand.companyName || "logo"} className={mini ? "h-6 object-contain" : "h-8 object-contain"} />
        ) : (
          <>
            <span className="text-[22px] font-extrabold text-primary tracking-tight">{mini ? (tenantBrand?.companyName || "CRE").slice(0, 2) : tenantBrand?.companyName || "CRE"}</span>
            {!mini && !tenantBrand && <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-[0.15em] mt-1.5">Ecosystem</span>}
          </>
        )}
      </div>

      {/* Collapse toggle — vertically centered on the sidebar edge (matches BD portal) */}
      <button
        onClick={toggle}
        title={mini ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-surface-container-lowest border border-outline-variant shadow-sm grid place-items-center text-on-surface-variant hover:bg-surface-container hover:text-primary z-50"
      >
        {mini ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      <nav className={`flex-1 space-y-1 ${mini ? "px-2" : "px-3"}`}>
        {CLIENT_NAV.filter((n) => featureEnabled(tenantBrand?.features, n.key)).map((n, idx) => {
          const isActive = n.key === active;
          const badge = n.key === "inbox" ? totalUnread : 0;
          return (
            <button
              key={n.key}
              ref={navRefs ? (el) => { navRefs.current[idx] = el; } : undefined}
              onClick={() => (n.path ? navigate(n.path) : soon())}
              title={mini ? n.label : undefined}
              className={`relative w-full flex items-center gap-3 py-2.5 lg:py-2 rounded-lg text-sm font-medium transition-colors ${mini ? "justify-center px-0" : "px-3"} ${isActive ? "bg-primary text-white shadow-sm" : "text-on-surface-variant hover:bg-surface-container-highest"}`}
            >
              <n.icon size={18} className="shrink-0" /> {!mini && n.label}
              {badge > 0 && (
                <span
                  className={`text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center shrink-0 ${
                    isActive ? "bg-white text-primary" : "bg-primary text-white"
                  } ${mini ? "absolute top-1 right-1" : "ml-auto"}`}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className={`py-4 border-t border-outline-variant space-y-1 ${mini ? "px-2" : "px-3"}`}>
        <button onClick={soon} title={mini ? "Settings" : undefined} className={`w-full flex items-center gap-3 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container-highest ${mini ? "justify-center px-0" : "px-3"}`}><Settings size={18} className="shrink-0" /> {!mini && "Settings"}</button>
        <button onClick={soon} title={mini ? "Support" : undefined} className={`w-full flex items-center gap-3 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container-highest ${mini ? "justify-center px-0" : "px-3"}`}><HelpCircle size={18} className="shrink-0" /> {!mini && "Support"}</button>
        <button onClick={logout} title={mini ? "Logout" : undefined} className={`w-full flex items-center gap-3 py-2 rounded-lg text-sm font-medium text-error hover:bg-error-container/40 ${mini ? "justify-center px-0" : "px-3"}`}><LogOut size={18} className="shrink-0" /> {!mini && "Logout"}</button>
      </div>
    </aside>
  );
};

export default ClientSidebar;
